import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { newsletterService } from '../services/api';

export default function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState(''); // Added for error messages

  // Placeholder for a translation function, as 't' is used in the instruction
  // In a real app, this would be imported from an i18n library (e.g., react-i18next)
  const t = (key) => {
    if (key === 'newsletter.error') return 'Something went wrong. Please try again.';
    return key;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');
    setErrorMessage(''); // Clear previous error message

    try {
      const response = await newsletterService.subscribe(email);
      
      if (response.status === 200 || response.status === 201) {
        setStatus('success');
        setEmail('');
        setTimeout(() => setStatus('idle'), 3000); // Reset status after success
      } else {
        setStatus('error');
        setErrorMessage(response.data?.message || t('newsletter.error'));
        setTimeout(() => setStatus('idle'), 3000); // Reset status after error
      }
    } catch (err) {
      console.error('Newsletter subscription error:', err); // Log the error
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <div className="mt-6">
      <h4 className="text-sm font-semibold text-raya-text-secondary mb-3">Subscribe to our newsletter</h4>
      <form onSubmit={handleSubmit} className="flex">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          disabled={status === 'success'}
          className="flex-1 bg-raya-card/50 backdrop-blur-xl border border-raya-border 
            focus:border-raya-accent focus:ring-2 focus:ring-raya-accent/20 
            rounded-l-xl px-4 py-3 text-raya-text-primary placeholder:text-raya-text-tertiary
            transition-all duration-300 focus:outline-none
            disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <button
          type="submit"
          disabled={status === 'loading' || status === 'success'}
          className="bg-gradient-to-r from-raya-accent to-raya-accent-hover 
            hover:from-raya-accent-hover hover:to-raya-accent
            disabled:bg-raya-border disabled:cursor-not-allowed
            px-6 py-3 rounded-r-xl text-raya-bg font-semibold
            transition-all transform hover:scale-105 active:scale-95
            flex items-center gap-2 whitespace-nowrap"
        >
          {status === 'loading' ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Subscribing...</span>
            </>
          ) : status === 'success' ? (
            <>
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Subscribed!</span>
            </>
          ) : (
            <>
              <span>Subscribe</span>
              <Send className="w-4 h-4" />
            </>
          )}
        </button>
      </form>
      {status === 'error' && (
        <p className="text-red-400 text-sm mt-2">{errorMessage || 'Please enter a valid email address'}</p>
      )}
      {status === 'success' && (
        <p className="text-green-400 text-sm mt-2">Thank you for subscribing! 🎉</p>
      )}
    </div>
  );
}
